import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    DialogTitle,
} from '@headlessui/react';
import { Color } from '../../lib/types.ts';
import { Button } from '../buttons/Button.tsx';
import {
    UilExternalLinkAlt,
    UilLock,
    UilTimes,
} from '@iconscout/react-unicons';
import { cn } from '../../lib/helpers.ts';
import { API_URL } from '../../lib/api.ts';
import { Link } from 'react-router';
import DOMPurify from 'dompurify';
import { useAuth } from '../../lib/auth.ts';

interface MethodologyDialogProps {
    isOpen: boolean;
    onClose: () => void;
    color?: Color | 'product';
    heading: string;
    content: string | null;
}

export default function MethodologyDialog({
    isOpen,
    onClose,
    heading,
    content,
    color = 'product',
}: MethodologyDialogProps) {
    const safeContent = DOMPurify.sanitize(content ?? '');

    const { userHasFullLicense, isLoading: userIsLoading } = useAuth({
        middleware: 'auth',
        redirectTo: '/',
        tmp: 'MethodologyDialog',
    });

    const disableExternalMethodologyButton =
        userIsLoading || !userHasFullLicense;

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} className="relative z-50">
                {/* The backdrop, rendered as a fixed sibling to the panel container */}
                <DialogBackdrop
                    transition
                    className={cn(
                        'fixed inset-0 bg-black/75',
                        'transition-opacity duration-200 data-[closed]:opacity-0'
                    )}
                />

                {/* Full-screen container to center the panel */}
                <div className="fixed inset-0 w-screen overflow-y-auto p-4">
                    <div className="flex min-h-full w-full items-center justify-center">
                        {/* The actual dialog panel  */}
                        <DialogPanel
                            transition
                            className={cn(
                                'w-full max-w-dialog-md rounded-xl bg-base-100 shadow-lg',
                                'transition-all duration-200 data-[closed]:translate-y-4 data-[closed]:scale-95 data-[closed]:opacity-0'
                            )}
                        >
                            <header className="flex justify-between gap-4 pb-6 pr-3 pt-3">
                                <DialogTitle className="mb-0 pl-8 pt-5" as="h3">
                                    {heading}
                                </DialogTitle>
                                <Button
                                    color={color}
                                    className={cn(
                                        'aspect-square h-11 w-11 p-3'
                                    )}
                                    variant="secondary"
                                    onClick={onClose}
                                >
                                    <UilTimes size={20} />
                                </Button>
                            </header>

                            <div className="px-8">
                                <div
                                    className="prose prose-blue"
                                    dangerouslySetInnerHTML={{
                                        __html: safeContent,
                                    }}
                                />

                                <footer className="flex justify-end gap-4 pb-8 pt-6">
                                    <Button
                                        color={color}
                                        variant="secondary"
                                        onClick={onClose}
                                    >
                                        Zavřít
                                    </Button>

                                    <Link
                                        to={`${API_URL}/methodology`}
                                        target="_blank"
                                        className="inline-block w-auto"
                                    >
                                        <Button
                                            color={color}
                                            variant="primary"
                                            disabled={
                                                disableExternalMethodologyButton
                                            }
                                            className={cn(
                                                disableExternalMethodologyButton &&
                                                    'relative after:absolute after:inset-0 after:z-10 after:h-full after:w-full after:rounded-2xl after:bg-black/50'
                                            )}
                                        >
                                            <UilExternalLinkAlt
                                                className="mr-2"
                                                size={20}
                                            />
                                            Metodika
                                            {disableExternalMethodologyButton && (
                                                <UilLock className="absolute left-1/2 top-1/2 z-20 -translate-x-1/2 -translate-y-1/2 text-white" />
                                            )}
                                        </Button>
                                    </Link>
                                </footer>
                            </div>
                        </DialogPanel>
                    </div>
                </div>
            </Dialog>
        </>
    );
}
